import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  createFromIdea,
  createProject,
  fetchGenerators,
  fetchMaterials,
  fetchPrinters,
  fetchTrends,
  generateFile,
  type GeneratorInfo,
  type TrendIdea,
} from "../api";
import { HeroBand } from "../components/HeroBand";
import { Icon } from "../components/Icon";
import { Modal } from "../components/ui";
import { useT } from "../i18n";
import { useToast } from "../lib/toast";
import { PartThumb } from "../scene/PartThumb";
import type { MaterialProfile, PrinterProfile } from "../types";

// Template taxonomy — maps the real generators into browsable families.
const CATS: { id: string; label: string; color: string; names: string[] }[] = [
  { id: "fix", label: "Fixation & support", color: "#34d399", names: ["bracket", "hook", "stand", "mount_puck", "tilt_mount", "camera_cradle"] },
  { id: "box", label: "Boîtiers & optique", color: "#56d4e3", names: ["enclosure", "lens_cap", "vase"] },
  { id: "mech", label: "Mécanique", color: "#fbbf24", names: ["gear", "pin", "arm_base", "arm_link", "arm_clevis"] },
  { id: "drone", label: "Drone & aéro", color: "#a78bfa", names: ["drone_arm", "drone_plate", "turbine_hub", "turbine_blade", "wing_section"] },
];
function catOf(name: string) {
  return CATS.find((c) => c.names.includes(name)) || CATS[0];
}
function catMaterial(id: string): string {
  return id === "mech" ? "PLA" : id === "box" ? "RESIN" : id === "drone" ? "TPU" : "PETG";
}

const EXAMPLES = [
  "Un support de caméra incliné à 60° pour rail 2020, butée avant et deux trous M3",
  "Un boîtier IP54 pour capteur, joint et passe-câble PG7, bossages à vis",
  "Un engrenage droit module 1.5, 24 dents, alésage 6 mm avec moyeu",
  "Un crochet mural charge 5 kg, deux vis, rayon de gorge confortable",
];
const GEN_EXAMPLES = [
  "Une figurine de dragon stylisée enroulée sur une base ronde, 80 mm",
  "Un cache-pot géométrique facetté avec drainage, diamètre 120 mm",
  "Un buste low-poly décoratif sur socle hexagonal",
];

type ParamVal = number | boolean;
function paramEntries(g: GeneratorInfo): [string, ParamVal][] {
  const props = g.params_schema.properties || {};
  return Object.entries(props).map(([k, v]) => {
    const d = v.default;
    // Preserve booleans (e.g. bracket.corner_holes, arm_link.second_bore) -- coercing them
    // to a number would silently disable the feature and render a number box for a toggle.
    if (typeof d === "boolean") return [k, d];
    return [k, typeof d === "number" ? d : 0];
  });
}
function unitOf(name: string): string {
  if (name.endsWith("_mm")) return "mm";
  if (name.endsWith("_deg")) return "°";
  return "";
}
function labelOf(name: string): string {
  return name.replace(/_(mm|deg)$/, "").replace(/_/g, " ");
}

interface Cfg {
  gen: GeneratorInfo;
  params: Record<string, ParamVal>;
  printer: string;
  material: string;
  name: string;
}

// Create funnel: choose a path (AI / template) -> configure -> INSPECTOR gate -> project.
export function CreatePage() {
  const tr = useT();
  const [gens, setGens] = useState<GeneratorInfo[]>([]);
  const [printers, setPrinters] = useState<PrinterProfile[]>([]);
  const [materials, setMaterials] = useState<MaterialProfile[]>([]);
  const [prompt, setPrompt] = useState("");
  const [name, setName] = useState("");
  const [mode, setMode] = useState<"parametric" | "generative">("parametric");
  const [printer, setPrinter] = useState("prusa_mk4_0.4");
  const [material, setMaterial] = useState("PLA");
  const [busy, setBusy] = useState(false);
  const [trends, setTrends] = useState<TrendIdea[]>([]);
  const [trendSource, setTrendSource] = useState<"llm" | "fallback" | "">("");
  const [trendBusy, setTrendBusy] = useState(false);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [cfg, setCfg] = useState<Cfg | null>(null);
  const nav = useNavigate();
  const [search] = useSearchParams();
  const toast = useToast((s) => s.show);

  useEffect(() => {
    const seed = search.get("idea");
    if (seed) setPrompt(seed);
  }, [search]);

  useEffect(() => {
    fetchGenerators().then(setGens).catch(() => {});
    fetchPrinters().then((ps) => { setPrinters(ps); if (ps[0]) setPrinter(ps[0].id); }).catch(() => {});
    fetchMaterials().then(setMaterials).catch(() => {});
    loadTrends();
  }, []);

  function loadTrends(theme?: string) {
    setTrendBusy(true);
    fetchTrends(8, theme)
      .then((r) => { setTrends(r.ideas); setTrendSource(r.source); })
      .catch(() => setTrends([]))
      .finally(() => setTrendBusy(false));
  }

  const matsFor = (pid: string): MaterialProfile[] => {
    const p = printers.find((x) => x.id === pid);
    const ids = p?.compatible_materials ?? materials.map((m) => m.id);
    return materials.filter((m) => ids.includes(m.id));
  };
  const compatMats = matsFor(printer);
  const buildVol = (pid: string): string => {
    const p = printers.find((x) => x.id === pid);
    return p ? p.build_volume_mm.map(Math.round).join(" × ") + " mm" : "";
  };

  async function designFromPrompt() {
    if (!prompt.trim()) return;
    setBusy(true);
    try {
      const proj = await createProject(name || prompt.slice(0, 48), prompt);
      const f = await createFromIdea(proj.id, { idea: prompt, mode, printer_profile: printer, material });
      toast(f.passed ? "Projet créé + pièce prête à imprimer" : "Projet créé · brouillon à revoir", f.passed ? "ok" : "bad");
      nav(`/studio?project=${proj.id}&file=${f.id}`);
    } catch (e) {
      const msg = String((e as Error).message);
      toast(/502|503|gener|unavail|CAD/i.test(msg) ? "IA non configurée (ajoute une clé / Ollama). Pars d'un template ci-dessous." : msg, "bad");
    } finally {
      setBusy(false);
    }
  }

  function openConfig(g: GeneratorInfo) {
    const params: Record<string, ParamVal> = {};
    paramEntries(g).forEach(([k, v]) => { params[k] = v; });
    setCfg({ gen: g, params, printer, material, name: "" });
  }

  async function createFromConfig() {
    if (!cfg) return;
    setBusy(true);
    try {
      const g = cfg.gen;
      const proj = await createProject(cfg.name || g.title, `Projet ${g.title}`);
      const f = await generateFile(proj.id, {
        generator: g.name, params: cfg.params, printer_profile: cfg.printer, material: cfg.material, name: g.title,
      });
      toast(f.passed ? "Projet créé + pièce générée + validée" : "Projet créé · pièce à revoir", f.passed ? "ok" : "bad");
      setCfg(null);
      nav(`/studio?project=${proj.id}&file=${f.id}`);
    } catch (e) {
      toast(String((e as Error).message), "bad");
    } finally {
      setBusy(false);
    }
  }

  function useTrend(t: TrendIdea) {
    setMode(t.mode);
    setPrompt(t.prompt);
    setName(t.title);
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast(`Idée chargée : ${t.title}`, "ok");
  }

  const templates = useMemo(
    () => gens.filter((g) => (cat === "all" || catOf(g.name).id === cat) && (g.title + g.description + g.name).toLowerCase().includes(q.toLowerCase())),
    [gens, cat, q],
  );
  const examples = mode === "parametric" ? EXAMPLES : GEN_EXAMPLES;

  return (
    <div>
      <HeroBand
        image="/showcase/h3.jpg"
        eyebrow="Atelier"
        title={tr("Donne vie à ton idée")}
        sub="Décris, génère, valide, imprime. Ton prochain modèle commence ici."
        ctaLabel="Voir mes projets"
        ctaTo="/projects"
        ctaIcon="projects"
      />
      <div style={{ height: 6 }} />

      {/* Funnel stepper */}
      <div className="card funnel" style={{ marginBottom: 22, padding: "16px 22px" }}>
        {([["01", "Choisis une voie", "IA ou template", "sparkle"], ["02", "Configure", "machine · matière · params", "generators"], ["03", "Validation INSPECTOR", "gate fail-closed → projet", "shield"]] as const).map((s, i, arr) => (
          <div key={s[0]} style={{ display: "contents" }}>
            <div className="row" style={{ gap: 13 }}>
              <div className="funnel-num">{s[0]}</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, fontFamily: "var(--font-display)" }}>{s[1]}</div>
                <div className="muted" style={{ fontSize: 11.5 }}>{s[2]}</div>
              </div>
            </div>
            {i < arr.length - 1 && <div className="funnel-line"><Icon name="arrowRight" size={16} /></div>}
          </div>
        ))}
      </div>

      {/* Voie A : composer IA */}
      <div className="card" style={{ marginBottom: 26, padding: 0, overflow: "hidden" }}>
        <div className="row between" style={{ padding: "14px 20px", borderBottom: "1px solid var(--line)" }}>
          <div className="row" style={{ gap: 9 }}><div style={{ width: 26, height: 26, borderRadius: 8, background: "var(--accent-soft)", border: "1px solid var(--accent-line)", display: "grid", placeItems: "center", color: "var(--accent-3)" }}><Icon name="sparkle" size={14} /></div><h2>{tr("Décris ton idée")}</h2></div>
          <span className="eyebrow">{tr("voie A · IA")}</span>
        </div>
        <div style={{ padding: 20 }}>
          <div className="row wrap" style={{ gap: 12, marginBottom: 16 }}>
            {([["parametric", "CAD paramétrique", "Le LLM écrit du code CAD exact, mesurable et réimprimable.", "generators"], ["generative", "Maillage génératif", "Génère une forme libre (fal.ai / kai.ai) — idéal déco & figurines.", "box"]] as const).map(([m, ti, de, ic]) => (
              <button key={m} onClick={() => setMode(m)} className={`pathcard ${mode === m ? "on" : ""}`}>
                <div className="row between" style={{ marginBottom: 7 }}>
                  <span style={{ width: 30, height: 30, borderRadius: 8, display: "grid", placeItems: "center", color: mode === m ? "var(--accent-3)" : "var(--muted)", background: mode === m ? "var(--accent-soft)" : "var(--surface-2)", border: `1px solid ${mode === m ? "var(--accent-line)" : "var(--line-2)"}` }}><Icon name={ic} size={16} /></span>
                  {mode === m && <span className="badge accent" style={{ fontSize: 10 }}><span className="dot" />actif</span>}
                </div>
                <div style={{ fontWeight: 600, fontSize: 14, fontFamily: "var(--font-display)" }}>{ti}</div>
                <div className="muted" style={{ fontSize: 12, marginTop: 4, lineHeight: 1.45 }}>{de}</div>
              </button>
            ))}
          </div>

          <div className="row wrap" style={{ gap: 12, marginBottom: 12 }}>
            <div className="field" style={{ marginBottom: 0, flex: 2, minWidth: 200 }}><label>{tr("Nom du projet (optionnel)")}</label><input value={name} onChange={(e) => setName(e.target.value)} placeholder={tr("Bras articulé, boîtier capteur…")} /></div>
            <div className="field" style={{ marginBottom: 0, flex: 1.4, minWidth: 180 }}>
              <label>{tr("Machine")}</label>
              <select value={printer} onChange={(e) => { const np = e.target.value; setPrinter(np); const ids = printers.find((x) => x.id === np)?.compatible_materials ?? []; if (ids.length && !ids.includes(material)) setMaterial(ids[0]); }}>{printers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
              <span className="mono" style={{ fontSize: 10.5, color: "var(--muted-2)" }}>{buildVol(printer)}</span>
            </div>
            <div className="field" style={{ marginBottom: 0, flex: 1, minWidth: 130 }}><label>{tr("Matière")}</label><select value={material} onChange={(e) => setMaterial(e.target.value)}>{compatMats.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select></div>
          </div>

          <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} style={{ minHeight: 104, width: "100%" }} placeholder={mode === "parametric" ? "Décris l'objet, ses cotes et contraintes…" : "Décris la forme à générer en 3D…"} />

          <div className="row wrap" style={{ gap: 7, marginTop: 11 }}>
            <span className="eyebrow" style={{ alignSelf: "center" }}>exemples</span>
            {examples.map((ex) => <button key={ex} className="ex-chip" onClick={() => setPrompt(ex)}>{ex.length > 52 ? ex.slice(0, 52) + "…" : ex}</button>)}
          </div>

          <div className="row between wrap" style={{ marginTop: 16, gap: 10 }}>
            <button className="btn primary" disabled={busy || !prompt.trim()} onClick={designFromPrompt}>{busy ? <><span className="spinner" /> {tr("Conception…")}</> : <><Icon name="sparkle" size={15} /> {tr("Créer le projet + concevoir")}</>}</button>
            <span className="muted row" style={{ gap: 6, fontSize: 12 }}><Icon name="shield" size={12} /> {tr("Routé via CreditsForge / ComputeForge / Ollama, exécuté + validé par l'INSPECTOR.")}</span>
          </div>
        </div>
      </div>

      {/* Voie B : bibliothèque de templates */}
      <div className="row between wrap" style={{ marginBottom: 14, gap: 10, alignItems: "flex-end" }}>
        <div>
          <div className="row" style={{ gap: 10, alignItems: "baseline" }}><h2>{tr("Bibliothèque de templates")}</h2><span className="badge"><span className="num">{templates.length}</span></span></div>
          <div className="eyebrow muted" style={{ marginTop: 6 }}>{tr("voie B · paramétrique déterministe · sans IA")}</div>
        </div>
        <div className="topsearch" style={{ maxWidth: 260 }}><Icon name="search" size={15} /><input placeholder="Chercher un template…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
      </div>

      <div className="row wrap" style={{ gap: 7, marginBottom: 16 }}>
        <button className={`fcat ${cat === "all" ? "on" : ""}`} onClick={() => setCat("all")}>Tous</button>
        {CATS.map((c) => <button key={c.id} className={`fcat ${cat === c.id ? "on" : ""}`} onClick={() => setCat(c.id)}><span className="dot" style={{ width: 7, height: 7, color: c.color }} />{c.label}</button>)}
      </div>

      <div className="grid cards" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(248px, 1fr))", marginBottom: 30 }}>
        {templates.map((g) => {
          const c = catOf(g.name);
          const pnames = Object.keys(g.params_schema.properties || {});
          return (
            <div key={g.name} className="card hover tpl" onClick={() => openConfig(g)}>
              <div className="row" style={{ gap: 13, marginBottom: 11 }}>
                <PartThumb material={catMaterial(c.id)} passed size={58} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="card-title" style={{ fontSize: 14 }}>{g.title}</div>
                  <span className="badge" style={{ marginTop: 6, color: c.color, borderColor: `color-mix(in oklab, ${c.color} 40%, var(--line-2))`, background: `color-mix(in oklab, ${c.color} 12%, transparent)` }}>{c.label}</span>
                </div>
              </div>
              <div className="muted" style={{ fontSize: 12, minHeight: 50, lineHeight: 1.45 }}>{g.description}</div>
              <div className="row wrap" style={{ gap: 5, marginTop: 10, marginBottom: 13 }}>{pnames.slice(0, 4).map((p) => <span key={p} className="kbd">{labelOf(p)}</span>)}{pnames.length > 4 && <span className="kbd">+{pnames.length - 4}</span>}</div>
              <button className="btn sm" style={{ width: "100%", justifyContent: "center" }}><Icon name="generators" size={14} /> Configurer</button>
            </div>
          );
        })}
        {templates.length === 0 && <div className="muted" style={{ padding: 24 }}>Aucun template ne correspond.</div>}
      </div>

      {/* Tendances */}
      <div className="row between wrap" style={{ marginBottom: 14, gap: 10 }}>
        <div className="row" style={{ gap: 10, alignItems: "baseline" }}><h2 style={{ margin: 0 }}>{tr("Tendances du moment")}</h2><span className="eyebrow">{tr(trendSource === "llm" ? "généré · IA" : "sélection")} · {tr("1 clic → prompt")}</span></div>
        <button className="btn" disabled={trendBusy} onClick={() => loadTrends()}>{trendBusy ? <><span className="spinner" /> …</> : <><Icon name="sparkle" size={14} /> {tr("Rafraîchir")}</>}</button>
      </div>
      <div className="grid cards" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}>
        {trends.map((t) => (
          <div key={t.title} className="card hover" style={{ cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }} onClick={() => !busy && useTrend(t)}>
            <div className="row between" style={{ marginBottom: 8 }}>
              <span className={`badge ${t.mode === "parametric" ? "accent" : "warn"}`} style={{ fontSize: 10.5 }}>{t.mode === "parametric" ? "CAD paramétrique" : "génératif"}</span>
              <span className="id">{t.category}</span>
            </div>
            <div className="card-title" style={{ fontSize: 13.5 }}>{t.title}</div>
            <div className="muted" style={{ fontSize: 12, marginTop: 5, minHeight: 34, lineHeight: 1.45 }}>{t.why}</div>
            <div className="row wrap" style={{ gap: 5, marginTop: 8 }}>{t.tags.slice(0, 4).map((tag) => <span key={tag} style={{ fontSize: 10.5, color: "var(--text-2)", background: "var(--surface-2)", border: "1px solid var(--line)", borderRadius: 6, padding: "1px 7px" }}>{tag}</span>)}</div>
          </div>
        ))}
      </div>

      {/* Template configurator (étape 02) */}
      {cfg && (
        <Modal
          title={`${tr("Configurer")} · ${cfg.gen.title}`}
          onClose={() => setCfg(null)}
          footer={<><button className="btn ghost" onClick={() => setCfg(null)}>{tr("Annuler")}</button><button className="btn primary" disabled={busy} onClick={createFromConfig}>{busy ? <><span className="spinner" /> {tr("Génération…")}</> : <><Icon name="zap" size={14} /> {tr("Générer + valider")}</>}</button></>}
        >
          <div className="row" style={{ gap: 16, alignItems: "flex-start", marginBottom: 16 }}>
            <PartThumb material={catMaterial(catOf(cfg.gen.name).id)} passed size={92} />
            <div style={{ flex: 1 }}>
              <span className="badge" style={{ color: catOf(cfg.gen.name).color, borderColor: `color-mix(in oklab, ${catOf(cfg.gen.name).color} 40%, var(--line-2))`, background: `color-mix(in oklab, ${catOf(cfg.gen.name).color} 12%, transparent)` }}>{catOf(cfg.gen.name).label}</span>
              <div className="muted" style={{ fontSize: 12.5, marginTop: 8, lineHeight: 1.5 }}>{cfg.gen.description}</div>
            </div>
          </div>
          <div className="field"><label>{tr("Nom du projet (optionnel)")}</label><input value={cfg.name} onChange={(e) => setCfg({ ...cfg, name: e.target.value })} placeholder={cfg.gen.title} autoFocus /></div>
          <div className="eyebrow" style={{ margin: "4px 0 9px" }}>{tr("Paramètres")}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
            {Object.keys(cfg.params).map((p) => {
              const val = cfg.params[p];
              if (typeof val === "boolean") {
                return (
                  <div className="field" key={p} style={{ marginBottom: 0 }}>
                    <label>{labelOf(p)}</label>
                    <button type="button" className={`btn sm ${val ? "primary" : "ghost"}`} style={{ width: "100%", justifyContent: "space-between" }} onClick={() => setCfg({ ...cfg, params: { ...cfg.params, [p]: !val } })}>
                      {val ? "Activé" : "Désactivé"} <Icon name={val ? "check" : "x"} size={13} />
                    </button>
                  </div>
                );
              }
              const unit = unitOf(p);
              return (
                <div className="field" key={p} style={{ marginBottom: 0 }}>
                  <label>{labelOf(p)}{unit ? ` (${unit})` : ""}</label>
                  <input type="number" value={val} onChange={(e) => setCfg({ ...cfg, params: { ...cfg.params, [p]: Number(e.target.value) } })} />
                </div>
              );
            })}
          </div>
          <div className="row" style={{ gap: 10 }}>
            <div className="field" style={{ flex: 1.4, marginBottom: 0 }}>
              <label>{tr("Machine")}</label>
              <select value={cfg.printer} onChange={(e) => { const np = e.target.value; const ids = printers.find((x) => x.id === np)?.compatible_materials ?? []; setCfg({ ...cfg, printer: np, material: ids.includes(cfg.material) ? cfg.material : ids[0] }); }}>{printers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
            </div>
            <div className="field" style={{ flex: 1, marginBottom: 0 }}><label>{tr("Matière")}</label><select value={cfg.material} onChange={(e) => setCfg({ ...cfg, material: e.target.value })}>{matsFor(cfg.printer).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select></div>
          </div>
          <div className="muted row" style={{ gap: 6, fontSize: 11.5, marginTop: 12 }}><Icon name="shield" size={12} /> {tr("Géométrie exacte via le CAD kernel, puis gate INSPECTOR avant ajout au projet.")}</div>
        </Modal>
      )}
    </div>
  );
}
